import React, { useCallback, useRef, useState } from "react";
import clsx from "clsx";
import { Box, Typography } from "@material-ui/core";
import { useModusTextFieldStyles } from "./ModusTextField.style";

export interface ModusTextFieldValidate {
  required?: boolean;
  requiredMessage?: string;
  minLength?: number;
  maxLength?: number;
  min?: number;
  max?: number;
  pattern?: string;
  patternMessage?: string;
  allowSpace?: boolean;
}

export interface ModusTextFieldProps {
  name: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  placeholder?: string;
  disabled?: boolean;
  readOnly?: boolean;
  type?: React.InputHTMLAttributes<HTMLInputElement>["type"];
  id?: string;
  autoFocus?: boolean;
  autoComplete?: string;
  label?: React.ReactNode;
  /** Shows red * after label */
  required?: boolean;
  maxCount?: number;
  hideCount?: boolean;
  labelStyle?: React.CSSProperties;
  error?: boolean;
  helperText?: string;
  className?: string;
  inputClassName?: string;
  validate?: ModusTextFieldValidate;
  setError?: (hasError: boolean) => void;
  /** Start validation as touched (e.g. required fields) */
  validateInitiallyTouched?: boolean;
  /** Live validation on key events */
  isOnTypeValidationEnabled?: boolean;
}

function runValidation(
  value: string,
  validate: ModusTextFieldValidate | undefined
): string {
  if (!validate) return "";
  if (validate.required && !value) {
    return validate.requiredMessage ?? "This field is required";
  }
  if (!validate.allowSpace && String(value).indexOf(" ") !== -1) {
    return "The input has one or more space(s). Please remove the space(s).";
  }
  if (validate.pattern && !new RegExp(validate.pattern).test(value)) {
    return validate.patternMessage ?? "Invalid format";
  }
  if (validate.minLength && value.length < validate.minLength) {
    return `The input must be at least ${validate.minLength} characters in length.`;
  }
  if (validate.maxLength && value.length > validate.maxLength) {
    return `The input exceeds the ${validate.maxLength} character limit.`;
  }
  if (validate.min !== undefined && value !== "" && Number(value) < validate.min) {
    return `Value must be at least ${validate.min}`;
  }
  if (validate.max !== undefined && value !== "" && Number(value) > validate.max) {
    return `Value must not exceed ${validate.max}`;
  }
  return "";
}

/**
 * Single-line text field aligned with {@link TextArea}: label row, optional count,
 * enabled border #171C1E, disabled = no border + #F2F4F7 background.
 */
export const ModusTextField: React.FC<ModusTextFieldProps> = ({
  name,
  value,
  onChange,
  onBlur,
  placeholder,
  disabled = false,
  readOnly = false,
  type = "text",
  id: idProp,
  autoFocus,
  autoComplete,
  label,
  required,
  maxCount,
  hideCount = false,
  labelStyle,
  error: errorProp,
  helperText: helperTextProp,
  className,
  inputClassName,
  validate,
  setError,
  validateInitiallyTouched = false,
  isOnTypeValidationEnabled = false,
}) => {
  const classes = useModusTextFieldStyles();
  const inputRef = useRef<HTMLInputElement>(null);
  const stableDomId = useRef(
    `modus-tf-${Math.random().toString(36).slice(2, 11)}`
  );
  const fieldId = idProp ?? stableDomId.current;
  const [internalError, setInternalError] = useState("");
  /** After blur (or validateInitiallyTouched), show borders/helper; before that, no error chrome */
  const [fieldBlurred, setFieldBlurred] = useState(
    () => Boolean(validateInitiallyTouched)
  );

  const handleBlur = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      setFieldBlurred(true);
      if (validate && !disabled && !readOnly) {
        const msg = runValidation(e.target.value, validate);
        setInternalError(msg);
        setError?.(msg !== "");
      }
      onBlur?.(e);
    },
    [onBlur, validate, disabled, readOnly, setError]
  );

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const v = e.target.value;
      if (!disabled && !readOnly && (fieldBlurred || isOnTypeValidationEnabled)) {
        const msg = runValidation(v, validate);
        setInternalError(msg);
        if (fieldBlurred || isOnTypeValidationEnabled) {
          setError?.(msg !== "");
        }
      }
      onChange(e);
    },
    [
      onChange,
      fieldBlurred,
      isOnTypeValidationEnabled,
      validate,
      setError,
      disabled,
      readOnly,
    ]
  );

  const handleKeyUp = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (disabled || readOnly || !isOnTypeValidationEnabled || !validate) return;
      const v = inputRef.current?.value ?? "";
      const msg = runValidation(v, validate);
      setInternalError(msg);
      if (fieldBlurred) {
        setError?.(msg !== "");
      }
    },
    [isOnTypeValidationEnabled, validate, setError, disabled, readOnly, fieldBlurred]
  );

  React.useEffect(() => {
    if (
      disabled ||
      readOnly ||
      !value ||
      !validate ||
      !fieldBlurred ||
      !isOnTypeValidationEnabled ||
      !setError
    ) {
      return;
    }
    const msg = runValidation(value, validate);
    setInternalError(msg);
    setError(msg !== "");
  }, [
    value,
    validate,
    fieldBlurred,
    isOnTypeValidationEnabled,
    setError,
    disabled,
    readOnly,
  ]);

  const showCount = maxCount != null && !hideCount;
  const hasValidationIssue =
    Boolean(errorProp) || Boolean(internalError);
  const hasError = fieldBlurred && hasValidationIssue;
  const displayHelper = hasError ? (helperTextProp ?? internalError) : "";

  return (
    <Box className={clsx(classes.root, className)}>
      {(label != null || required || showCount) && (
        <Box className={classes.headerRow}>
          <Box className={classes.labelGroup}>
            {label != null && (
              <Typography
                component="span"
                className={clsx(
                  classes.label,
                  disabled && !labelStyle && classes.labelDisabled
                )}
                style={labelStyle}
              >
                {label}
              </Typography>
            )}
            {required && <span className={classes.requiredMark}>*</span>}
          </Box>
          {showCount && (
            <Typography
              component="span"
              className={clsx(
                classes.charCount,
                disabled && classes.charCountDisabled
              )}
            >
              {value.length} / {maxCount}
            </Typography>
          )}
        </Box>
      )}

      <Box
        className={clsx(
          classes.fieldShell,
          hasError && !disabled && !readOnly && classes.fieldShellError,
          (disabled || readOnly) && classes.fieldShellDisabled
        )}
      >
        <input
          ref={inputRef}
          id={fieldId}
          name={name}
          type={type}
          value={value}
          onChange={handleChange}
          onBlur={handleBlur}
          onKeyUp={handleKeyUp}
          placeholder={placeholder}
          disabled={disabled}
          readOnly={readOnly}
          autoFocus={autoFocus}
          autoComplete={autoComplete}
          aria-invalid={hasError}
          aria-describedby={displayHelper ? `${fieldId}-helper` : undefined}
          className={clsx(
            classes.input,
            disabled && classes.inputDisabled,
            inputClassName
          )}
        />
      </Box>

      {displayHelper ? (
        <Typography
          id={`${fieldId}-helper`}
          component="p"
          className={clsx(hasError && classes.helper)}
          role={hasError ? "alert" : undefined}
        >
          {displayHelper}
        </Typography>
      ) : null}
    </Box>
  );
};

export default ModusTextField;
